import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import AuthContext from '../../context/App/AuthContext';
import UserProfilePanel from '../User/UserProfilePanel';

const NAV_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/courses', label: 'Courses' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const { isAuthenticated, user, getUser } = useContext(AuthContext);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && !user) getUser();
  }, [isAuthenticated, user, getUser]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const initials = user?.name ? user.name.charAt(0).toUpperCase() : 'A';

  return (
    <>
      <header className="relative z-30 bg-paper-soft">
        <div className="rule" />
        <div className="max-w-editorial mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-baseline gap-2 group">
              <span className="font-display text-2xl tracking-tight">
                Shubh<span className="text-saffron">Jyotish</span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `font-mono text-[12px] tracking-[0.18em] uppercase pb-1 ${
                      isActive ? 'text-ink link-underline-active' : 'text-ink-soft link-underline'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <button
                  type="button"
                  onClick={openPanel}
                  className="flex items-center gap-3 group"
                  aria-label="Open admin panel"
                >
                  <span className="hidden md:inline eyebrow text-ink-soft">Admin</span>
                  <span className="w-9 h-9 border border-ink flex items-center justify-center font-display text-sm group-hover:bg-ink group-hover:text-paper-soft transition-colors">
                    {initials}
                  </span>
                </button>
              ) : (
                <Link
                  to="/login"
                  className="hidden md:inline font-mono text-[11px] tracking-[0.22em] uppercase text-ink-muted hover:text-ink transition-colors pb-1 link-underline"
                >
                  Admin login
                </Link>
              )}

              <button
                type="button"
                onClick={() => setIsMobileOpen((v) => !v)}
                className="lg:hidden p-2 -mr-2 text-ink"
                aria-label="Toggle menu"
                aria-expanded={isMobileOpen}
              >
                {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
        <div className="rule" />

        {isMobileOpen && (
          <div className="lg:hidden bg-paper-soft border-b border-hairline">
            <nav className="flex flex-col px-6 py-6 gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `font-display text-2xl py-3 ${isActive ? 'text-saffron' : 'text-ink'}`
                  }
                  onClick={() => setIsMobileOpen(false)}
                >
                  {link.label}
                </NavLink>
              ))}
              {!isAuthenticated && (
                <Link
                  to="/login"
                  onClick={() => setIsMobileOpen(false)}
                  className="font-mono text-[11px] tracking-[0.22em] uppercase text-ink-muted py-3 mt-4 border-t border-hairline"
                >
                  Admin login
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      {isAuthenticated && (
        <UserProfilePanel isOpen={isPanelOpen} onClose={closePanel} />
      )}
    </>
  );
};

export default React.memo(Navbar);
